import { slopeAndCurvature } from '../track/elevation';
import type { SimModifierHooks } from '../track/modifiers';
import { Surface, type TrackData } from '../track/types';

/**
 * The simulation's view of a track: flat typed arrays derived from TrackData,
 * plus projection of world points onto the centerline polyline. Projection
 * uses a hint (the last known segment) and a small search window, so the car's
 * track position is continuous even where two parts of the circuit run close
 * together.
 */
export interface TrackRuntime {
  data: TrackData;
  n: number;
  length: number;
  halfWidth: number;
  curbWidth: number;
  px: Float64Array;
  py: Float64Array;
  tx: Float64Array;
  ty: Float64Array;
  s: Float64Array;
  /** Segment i runs from sample i to sample (i + 1) % n. */
  segDx: Float64Array;
  segDy: Float64Array;
  segLen: Float64Array;
  segInvLen2: Float64Array;
  curbLeft: Uint8Array;
  curbRight: Uint8Array;
  runoffLeft: Uint8Array;
  runoffRight: Uint8Array;
  barrierLeft: Float64Array;
  barrierRight: Float64Array;
  /** Runoff aprons beyond the curb (surface code, 0 = none) and their widths; zeros on older circuits. */
  apronLeft: Uint8Array;
  apronRight: Uint8Array;
  apronWidthLeft: Float64Array;
  apronWidthRight: Float64Array;
  sectorS: [number, number];
  /** Rise per metre along the track at each sample (uphill positive). */
  grade: Float64Array;
  /** Vertical curvature at each sample, 1/m: positive in dips (compressions), negative over crests. */
  vcurv: Float64Array;
  /** Banking per sample (slope across the road, left side higher positive); zeros when there is none. */
  bank: Float64Array;
  /** Signed centreline curvature (left positive), for the banking's load. */
  curv: Float64Array;
  /** Deterministic hazards from track modifiers (Phase 3 chaos cards). */
  hooks: SimModifierHooks[];
}

export function buildRuntime(data: TrackData, hooks: SimModifierHooks[] = []): TrackRuntime {
  const n = data.n;
  const px = Float64Array.from(data.px);
  const py = Float64Array.from(data.py);
  const segDx = new Float64Array(n);
  const segDy = new Float64Array(n);
  const segLen = new Float64Array(n);
  const segInvLen2 = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const j = i + 1 === n ? 0 : i + 1;
    const dx = px[j] - px[i];
    const dy = py[j] - py[i];
    const l2 = dx * dx + dy * dy;
    segDx[i] = dx;
    segDy[i] = dy;
    segLen[i] = Math.sqrt(l2);
    segInvLen2[i] = l2 > 0 ? 1 / l2 : 0;
  }
  const { grade, vcurv } = slopeAndCurvature(data.elev, data.length);
  return {
    data,
    n,
    length: data.length,
    halfWidth: data.halfWidth,
    curbWidth: data.curbWidth,
    px,
    py,
    tx: Float64Array.from(data.tx),
    ty: Float64Array.from(data.ty),
    s: Float64Array.from(data.s),
    segDx,
    segDy,
    segLen,
    segInvLen2,
    curbLeft: Uint8Array.from(data.curbLeft),
    curbRight: Uint8Array.from(data.curbRight),
    runoffLeft: Uint8Array.from(data.runoffLeft),
    runoffRight: Uint8Array.from(data.runoffRight),
    barrierLeft: Float64Array.from(data.barrierLeft),
    barrierRight: Float64Array.from(data.barrierRight),
    apronLeft: data.apronLeft ? Uint8Array.from(data.apronLeft) : new Uint8Array(n),
    apronRight: data.apronRight ? Uint8Array.from(data.apronRight) : new Uint8Array(n),
    apronWidthLeft: data.apronWidthLeft ? Float64Array.from(data.apronWidthLeft) : new Float64Array(n),
    apronWidthRight: data.apronWidthRight ? Float64Array.from(data.apronWidthRight) : new Float64Array(n),
    sectorS: [data.s[data.sectorIndex[0]], data.s[data.sectorIndex[1]]],
    grade: Float64Array.from(grade),
    vcurv: Float64Array.from(vcurv),
    bank: data.bank ? Float64Array.from(data.bank) : new Float64Array(n),
    curv: Float64Array.from(data.curvature),
    hooks,
  };
}

export interface Projection {
  /** Segment index. */
  index: number;
  /** Position along the segment, 0..1. */
  t: number;
  /** Arc length along the centerline, [0, length). */
  s: number;
  /** Signed lateral offset, left positive. */
  d: number;
  /** Unit tangent (interpolated between samples). */
  tx: number;
  ty: number;
}

export function makeProjection(): Projection {
  return { index: 0, t: 0, s: 0, d: 0, tx: 1, ty: 0 };
}

function projectSegment(rt: TrackRuntime, i: number, x: number, y: number, out: Projection): number {
  const ax = rt.px[i];
  const ay = rt.py[i];
  const dx = rt.segDx[i];
  const dy = rt.segDy[i];
  let t = ((x - ax) * dx + (y - ay) * dy) * rt.segInvLen2[i];
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const qx = ax + dx * t;
  const qy = ay + dy * t;
  const ex = x - qx;
  const ey = y - qy;
  out.index = i;
  out.t = t;
  return ex * ex + ey * ey;
}

function finish(rt: TrackRuntime, x: number, y: number, out: Projection): void {
  const i = out.index;
  const t = out.t;
  const j = i + 1 === rt.n ? 0 : i + 1;
  const len = rt.segLen[i];
  const ax = rt.px[i];
  const ay = rt.py[i];
  // Signed distance from the segment's supporting line (exact, left positive).
  out.d = len > 0 ? (rt.segDx[i] * (y - ay) - rt.segDy[i] * (x - ax)) / len : 0;
  let s = rt.s[i] + len * t;
  if (s >= rt.length) s -= rt.length;
  out.s = s;
  const tx = rt.tx[i] + (rt.tx[j] - rt.tx[i]) * t;
  const ty = rt.ty[i] + (rt.ty[j] - rt.ty[i]) * t;
  const tl = Math.sqrt(tx * tx + ty * ty);
  out.tx = tx / tl;
  out.ty = ty / tl;
}

const scratch = makeProjection();

/** Nearest point among segments hint-window .. hint+window. */
export function projectLocal(
  rt: TrackRuntime,
  x: number,
  y: number,
  hint: number,
  window: number,
  out: Projection,
): Projection {
  const n = rt.n;
  let best = Infinity;
  let bestI = hint;
  let bestT = 0;
  for (let k = -window; k <= window; k++) {
    let i = hint + k;
    if (i < 0) i += n;
    else if (i >= n) i -= n;
    const d2 = projectSegment(rt, i, x, y, scratch);
    if (d2 < best) {
      best = d2;
      bestI = i;
      bestT = scratch.t;
    }
  }
  out.index = bestI;
  out.t = bestT;
  finish(rt, x, y, out);
  return out;
}

/** Nearest point over the whole circuit (used when placing the car). */
export function projectGlobal(rt: TrackRuntime, x: number, y: number, out: Projection): Projection {
  let best = Infinity;
  let bestI = 0;
  let bestT = 0;
  for (let i = 0; i < rt.n; i++) {
    const d2 = projectSegment(rt, i, x, y, scratch);
    if (d2 < best) {
      best = d2;
      bestI = i;
      bestT = scratch.t;
    }
  }
  out.index = bestI;
  out.t = bestT;
  finish(rt, x, y, out);
  return out;
}

function nearestSample(rt: TrackRuntime, p: Projection): number {
  if (p.t < 0.5) return p.index;
  return p.index + 1 === rt.n ? 0 : p.index + 1;
}

/** Surface under a projected point. */
export function surfaceAt(rt: TrackRuntime, p: Projection): Surface {
  const ad = p.d < 0 ? -p.d : p.d;
  if (ad <= rt.halfWidth) return Surface.Asphalt;
  const k = nearestSample(rt, p);
  const left = p.d >= 0;
  const curb = (left ? rt.curbLeft[k] : rt.curbRight[k]) === 1;
  if (ad <= rt.halfWidth + rt.curbWidth && curb) return Surface.Curb;
  // A runoff apron just beyond the curb (or the edge), where the bot runs to the white line.
  const apron = left ? rt.apronLeft[k] : rt.apronRight[k];
  if (apron !== 0 && ad <= rt.halfWidth + (curb ? rt.curbWidth : 0) + (left ? rt.apronWidthLeft[k] : rt.apronWidthRight[k])) return apron as Surface;
  const run = left ? rt.runoffLeft[k] : rt.runoffRight[k];
  return run === Surface.Gravel ? Surface.Gravel : run === Surface.Runoff ? Surface.Runoff : Surface.Grass;
}

/** Lateral distance from the centerline to the barrier on the side of p. */
export function barrierAt(rt: TrackRuntime, p: Projection): number {
  const i = p.index;
  const j = i + 1 === rt.n ? 0 : i + 1;
  const arr = p.d >= 0 ? rt.barrierLeft : rt.barrierRight;
  return arr[i] + (arr[j] - arr[i]) * p.t;
}

/** Point on the centerline at arc length s, with tangent. */
export function pointAt(rt: TrackRuntime, s: number): { x: number; y: number; tx: number; ty: number; index: number } {
  let ss = s % rt.length;
  if (ss < 0) ss += rt.length;
  // Binary search the sample whose s is <= ss.
  let lo = 0;
  let hi = rt.n - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (rt.s[mid] <= ss) lo = mid;
    else hi = mid - 1;
  }
  const i = lo;
  const j = i + 1 === rt.n ? 0 : i + 1;
  const len = rt.segLen[i];
  const t = len > 0 ? (ss - rt.s[i]) / len : 0;
  const tx = rt.tx[i] + (rt.tx[j] - rt.tx[i]) * t;
  const ty = rt.ty[i] + (rt.ty[j] - rt.ty[i]) * t;
  const tl = Math.sqrt(tx * tx + ty * ty);
  return { x: rt.px[i] + rt.segDx[i] * t, y: rt.py[i] + rt.segDy[i] * t, tx: tx / tl, ty: ty / tl, index: i };
}
